import React, { useEffect, useRef } from "react";
import { AdyenCheckout, Dropin, Card, ApplePay } from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import { Loader2 } from "lucide-react";

/**
 * Adyen Web Drop-in v6 sessions-flow renderer.
 * `data` is the object returned by POST /api/checkout/offer/{slug}:
 *   { reference, session:{id,sessionData}, clientKey, environment, amount }
 */
export default function AdyenCheckoutBlock({ data, locale = "en", onDone }) {
  const hostRef = useRef(null);
  const dropinRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function mount() {
      const shopperLocale = locale?.startsWith("fr") ? "fr-CH" : "en-CH";
      try {
        const checkout = await AdyenCheckout({
          environment: data.environment || "test",
          clientKey: data.clientKey,
          countryCode: "CH",
          locale: shopperLocale,
          session: { id: data.session.id, sessionData: data.session.sessionData },
          onPaymentCompleted: (result) => {
            const ok = ["Authorised", "Received", "Pending"].includes(result?.resultCode);
            if (ok && onDone) onDone(result);
          },
          onPaymentFailed: () => {},
          onError: (err) => {
            console.warn("Adyen error", err);
          },
        });
        if (cancelled || !hostRef.current) return;
        dropinRef.current = new Dropin(checkout, {
          paymentMethodComponents: [Card, ApplePay],
          showPayButton: true,
        }).mount(hostRef.current);
      } catch (e) {
        console.warn("Adyen mount failed", e);
      }
    }
    mount();

    return () => {
      cancelled = true;
      try {
        dropinRef.current?.unmount?.();
      } catch {
        /* ignore */
      }
    };
  }, [data, locale, onDone]);

  return (
    <div data-testid="adyen-dropin">
      <div ref={hostRef} className="bg-white rounded-xl p-2 text-oj-ink" />
      {!dropinRef.current && (
        <div className="flex items-center justify-center gap-2 text-white/70 text-sm mt-3">
          <Loader2 className="size-4 animate-spin" /> Loading secure checkout…
        </div>
      )}
    </div>
  );
}
